import * as Api from '@ViewModels';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { LevStandardQueryMeta, reactQueryClient } from '../../../web/ReactQueryProvider';

export const ADMIN_ACCOUNT_QUERY_KEY = 'admin/account/<accountId>';

const getAdminAccountQueryOptions = (
	{
		accountId,
		meta,
	}: {
		accountId: string;
		meta?: LevStandardQueryMeta;
	},
	userSession: ReturnType<typeof useUserSession>
) => ({
	queryFn: () =>
		userSession.webServiceHelper.callAsync<Api.IAccount>(`admin/account/${encodeURIComponent(accountId)}`, 'GET'),
	queryKey: [ADMIN_ACCOUNT_QUERY_KEY, accountId],
	meta,
});

export function useAdminAccountQuery({
	enabled,
	accountId,
	meta,
}: {
	enabled?: boolean;
	accountId: string;
	meta?: LevStandardQueryMeta;
}) {
	const userSession = useUserSession();
	return useQuery({
		...getAdminAccountQueryOptions({ accountId, meta }, userSession),
		enabled,
	});
}

export function useAdminAccountSuspenseQuery({ accountId, meta }: { accountId: string; meta?: LevStandardQueryMeta }) {
	const userSession = useUserSession();
	return useSuspenseQuery(getAdminAccountQueryOptions({ accountId, meta }, userSession));
}

export const invalidateAccountAdminQuery = () => {
	reactQueryClient.invalidateQueries({ queryKey: [ADMIN_ACCOUNT_QUERY_KEY] });
};

/**
 * Publish an account a write already persisted straight into the cache. Invalidating alone leaves the
 * old account readable until the refetch lands, which is long enough for a concurrent full-account PUT
 * to pick up the stale copy and undo the write.
 */
export const setAccountAdminQueryData = (account: Api.IAccount) => {
	if (account?.id) {
		reactQueryClient.setQueryData([ADMIN_ACCOUNT_QUERY_KEY, account.id], account);
	}
};
